import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Shield, LogOut, Monitor, Smartphone, Globe, Clock, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDeviceIcon = (userAgent) => {
  if (!userAgent) return <Monitor className="h-4 w-4" />;
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return <Smartphone className="h-4 w-4" />;
  return <Monitor className="h-4 w-4" />;
};

const getBrowserName = (userAgent) => {
  if (!userAgent) return 'Unknown Browser';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Browser';
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function SecuritySettings() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/sessions`);
      setSessions(res.data || []);
    } catch {
      toast.error('Failed to load session history');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAll = async () => {
    setLogoutAllLoading(true);
    try {
      await axios.post(`${API}/auth/logout-all-devices`);
      toast.success('All devices logged out. Please log in again.');
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 1500);
    } catch {
      toast.error('Failed to logout all devices');
    } finally {
      setLogoutAllLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-semibold text-slate-800">Login Security</h1>
        </div>

        {/* Logout All Devices */}
        <Card className="border-red-100">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-medium text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Logout from All Devices
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  This will immediately invalidate all active sessions on every device.
                  You will need to log in again on each device.
                </p>
              </div>
              {showConfirm ? (
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setShowConfirm(false)}>Cancel</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleLogoutAll} disabled={logoutAllLoading}
                    data-testid="confirm-logout-all-btn">
                    {logoutAllLoading ? 'Logging out...' : 'Yes, Logout All'}
                  </Button>
                </div>
              ) : (
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-600 hover:border-red-600 flex-shrink-0"
                  onClick={() => setShowConfirm(true)}
                  data-testid="logout-all-devices-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout All Devices
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Session History */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                Recent Login Activity
              </span>
              <Button variant="ghost" size="sm" onClick={fetchSessions} className="text-white text-xs h-7 bg-blue-500 hover:bg-blue-600">
                Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">No login history found</div>
            ) : (
              <div className="divide-y">
                {sessions.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-4 px-5 py-3">
                    <div className={`p-2 rounded-full ${s.is_active ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                      {getDeviceIcon(s.user_agent)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-700">
                          {getBrowserName(s.user_agent)}
                        </p>
                        {i === 0 && (
                          <Badge className="bg-green-100 text-green-700 text-xs px-1.5 h-4" data-testid="current-session-badge">
                            Current
                          </Badge>
                        )}
                        {!s.is_active && <span className="text-xs text-slate-400">(Logged out)</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {s.ip_address || 'Unknown IP'}
                        </span>
                        <span>{formatDate(s.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
